/**
 * PackGrid — the nine cells, as three labelled reading rows of three styles.
 *
 * The Toile counterpart of narcis's/ovid's own `galleryProof` block (three labelled rows of three
 * portraits, `PackLandingTemplate.tsx`) plus its `PackGrid.module.scss` cell rules. Narcis's own
 * `PackGrid` component is a different thing entirely — the delivered-slot grid (streaming frames,
 * retry countdowns, failure cells, a per-image download pill) — none of which exists here: every
 * Toile cell is a rendered sample on disk, so this file's name collides with narcis's but its job
 * matches narcis's inline `pack.rows.map` gallery instead.
 *
 * Canon convergence (pack page canon convergence, overriding the 2026-08-30 showcase-cut plan's
 * decision 3 per operator instruction 2026-09-01): the per-cell recipe text (subject/style/palette/
 * framing), rendered as visible chips under every cell, is gone — narcis/ovid's own galleryProof
 * cells are a bare `<img>` with a descriptive alt, nothing else. The recipe still reaches search
 * engines: the alt text below carries the same four handles, and the route's `ImageGallery` JSON-LD
 * repeats them as each image's `caption`.
 *
 * Clicking a cell selected it in the stage above — the grid and the stage share one selection, owned
 * by PackLandingTemplate. Wall plan e turned that tap into a QUICK LOOK where the wall has a tile
 * for the cell: it lands on the wall armed on that cell, nothing fired (BRIEF §9). The stage's own
 * thumb strip keeps the selection — it is how the stage is browsed, and the corner tag follows it
 * (board `ToilePageToWall` frame 1).
 */

import {TOILE_PACK_SAMPLES_BASE_URL} from '@product/toile/config'
import {packCells, packRatio, type ToilePack} from './data'
import styles from './PackGrid.module.scss'

interface PackGridProps {
    pack: ToilePack
    onSelect: (index: number) => void
    /**
     * Whether a tap leaves the page. Wall plan e made the 3x3 a QUICK LOOK — "tapping a cell lands
     * on the wall armed on that cell, nothing fired" (BRIEF §9 / § the edges) — so the caller sends
     * the visitor to `/?basis=<tile>` instead of selecting the cell in the stage above. It falls
     * back to the shipped selection for a pack the wall holds no tiles for, and the cell has to say
     * which of the two it is doing: a screen reader is told "show it above" or "open it on the
     * wall", never one when it means the other.
     */
    travels?: boolean
}

export function PackGrid({pack, onSelect, travels = false}: PackGridProps) {
    const cells = packCells(pack)
    const base = `${TOILE_PACK_SAMPLES_BASE_URL}/${pack.slug}`

    return (
        <>
            {pack.readings.map((reading, rowIdx) => (
                <div key={reading.key} className={styles.readingRow}>
                    <div className={styles.readingLabel}>
                        <span>Reading {rowIdx + 1}</span>
                        <b>{reading.label}</b>
                    </div>
                    <div className={styles.readingGrid}>
                        {pack.styles.map((style, colIdx) => {
                            const i = rowIdx * 3 + colIdx
                            const c = cells[i]
                            const r = packRatio(pack, style.key)
                            return (
                                <button
                                    type="button"
                                    key={style.key}
                                    className={styles.cell}
                                    onClick={() => onSelect(i)}
                                    aria-label={
                                        travels
                                            ? `Change ${reading.label}, ${style.label}`
                                            : `Show ${reading.label}, ${style.label} above`
                                    }
                                >
                                    <div
                                        className={styles.cellImg}
                                        style={{aspectRatio: `${r.w} / ${r.h}`}}
                                    >
                                        <img
                                            src={`${base}/${c.image}`}
                                            alt={`${pack.name} — ${c.subject}, ${c.style}. ${c.palette}, ${c.framing}.`}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </div>
                                </button>
                            )
                        })}
                    </div>
                </div>
            ))}
        </>
    )
}
